import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

export const IMAGE_VARIABLE_WIDTH_LEGACY_FIELD_ORDER = [
  "image",
  "use_container",
  "property",
  "width",
  "background_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  imageVariableWidthBlockDescriptor: _generatedImageVariableWidthBlockDescriptor,
  imageVariableWidthBlockBlocDefinition,
  imageVariableWidthBlockDashboardLayout,
  imageVariableWidthBlockConfig,
  imageVariableWidthBlockResolveConfig,
  imageVariableWidthBlockProperties,
} = doodBlockConfig(
  {
    name: "Image - Variable width",
    componentType: "ImageVariableWidthBlockComponent",
    category: "media",
    description: "Image with configurable width.",
    keywords: ["image", "media", "width"],
    sharedFields: {},
    /**
     * New blocs start without container/padding. Saved blocs without this key
     * keep the property default (`true`) so existing pages do not change layout.
     */
    creationDefaults: {
      use_container: false,
    },
  },

  () => ({
    image: propertyValue({
      type: "image",
      value: null,
      options: {
        label: "pages.blocks.image-variable-width.image.label",
        help: "pages.blocks.image-variable-width.image.help",
      },
    }),
    use_container: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.image-variable-width.use-container.label",
        help: "pages.blocks.image-variable-width.use-container.help",
      },
    }),
    property: propertyValue({
      type: "select",
      value: "percent",
      options: {
        label: "pages.blocks.image-variable-width.property.label",
        help: "pages.blocks.image-variable-width.property.help",
        choices: [
          {
            value: "percent",
            label: "pages.blocks.image-variable-width.property.options.percent",
          },
          {
            value: "pixel",
            label: "pages.blocks.image-variable-width.property.options.pixel",
          },
        ],
      },
    }),
    width: propertyValue({
      type: "number",
      value: 50,
      options: {
        label: "pages.blocks.image-variable-width.width.label",
        help: "pages.blocks.image-variable-width.width.help",
      },
    }),
    background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "transparent" },
      options: {
        label: "pages.blocks.image-variable-width.background-color.label",
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.image-variable-width.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.image-variable-width.fieldset.image.label",
            fields: ["image", "use_container"],
          },
          {
            legend: "pages.blocks.image-variable-width.fieldset.size.label",
            fields: ["property", "width"],
          },
          {
            legend: "pages.blocks.image-variable-width.fieldset.background.label",
            fields: ["background_color"],
          },
        ],
      },
    ],
  }),
);

export function imageVariableWidthBlockDescriptor(): IBlockDescriptor;
export function imageVariableWidthBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function imageVariableWidthBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedImageVariableWidthBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedImageVariableWidthBlockDescriptor,
    imageVariableWidthBlockProperties,
    fieldOrder,
  );
}

export {
  imageVariableWidthBlockBlocDefinition,
  imageVariableWidthBlockDashboardLayout,
  imageVariableWidthBlockConfig,
  imageVariableWidthBlockResolveConfig,
  imageVariableWidthBlockProperties,
};

export type ImageVariableWidthBlockConfig = typeof imageVariableWidthBlockConfig;
